import React, {useReducer, useEffect, useContext, useState} from 'react';
import PropTypes from 'prop-types';
import {NavLink, Link} from 'react-router-dom';
import {Navbar} from 'react-bootstrap';
import {IconButton, Badge, Menu, MenuItem} from '@material-ui/core';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Help as HelpIcon,
  ExitToApp as ExitToAppIcon,
  Menu as MenuIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  ChromeReaderMode as ChromeReaderModeIcon,
  Description as DescriptionIcon,
  Bookmark as BookmarkIcon,
  LibraryBooks as LibraryBooksIcon,
  List as ListIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Message as MessageIcon,
  Comment as CommentIcon,
  ListAlt as ListAltIcon,
} from '@material-ui/icons';
import {isEmpty} from 'lodash/lang';
import {ImageWrapper, DropDownMenu} from 'components/common';
import ArchivedStatus from '../common/ArchivedStatus';
import {NotificationContext} from 'contexts/notifications';
import {fetchUnreadMessageCnt} from 'api/messages';
import {buildUserFullName, formatRole} from 'utils/common';
import {isReader, isSchoolCoordinatorOrTeacher} from 'utils/permissions';

const sideBarItems = {
  dashboard: () => (
    <li>
      <Link to="/">
        <DashboardIcon className="nav-sidebar-icon" />
        Dashboard
      </Link>
    </li>
  ),
  users: () => (
    <li>
      <Link to="/users">
        <PeopleIcon className="nav-sidebar-icon" />
        Users
      </Link>
    </li>
  ),
  schools: () => (
    <li>
      <Link to="/schools">
        <SchoolIcon className="nav-sidebar-icon" />
        Schools
      </Link>
    </li>
  ),
  classrooms: () => (
    <li>
      <Link to="/classrooms">
        <CommentIcon className="nav-sidebar-icon" />
        Classrooms
      </Link>
    </li>
  ),
  adminReadings: () => (
    <li>
      <Link to="/readings">
        <ClassIcon className="nav-sidebar-icon" />
        Readings
      </Link>
    </li>
  ),
  subscriptions: () => (
    <li>
      <Link to="/subscriptions">
        <ChromeReaderModeIcon className="nav-sidebar-icon" />
        Subscriptions
      </Link>
    </li>
  ),
  orders: () => (
    <li>
      <Link to="/orders">
        <DescriptionIcon className="nav-sidebar-icon" />
        Orders
      </Link>
    </li>
  ),
  messages: ({unreadCnt}) => (
    <li>
      <Link to="/messages">
        <MessageIcon className="nav-sidebar-icon" />
        <Badge color="secondary" badgeContent={unreadCnt} invisible={!unreadCnt}>
          Messages
        </Badge>
      </Link>
    </li>
  ),
  receivedMessages: ({unreadCnt}) => (
    <li>
      <Link to="/messages/received">
        <MessageIcon className="nav-sidebar-icon" />
        <Badge color="secondary" badgeContent={unreadCnt} invisible={!unreadCnt}>
          Messages
        </Badge>
      </Link>
    </li>
  ),
  readings: () => (
    <li>
      <Link to="/readings">
        <BookmarkIcon className="nav-sidebar-icon" />
        Readings
      </Link>
    </li>
  ),
  books: () => (
    <li>
      <Link to="/books">
        <LibraryBooksIcon className="nav-sidebar-icon" />
        Books
      </Link>
    </li>
  ),
  tips: () => (
    <li>
      <Link to="/tips">
        <ListIcon className="nav-sidebar-icon" />
        Tips
      </Link>
    </li>
  ),
  awards: () => (
    <li>
      <Link to="/awards">
        <PhotoLibraryIcon className="nav-sidebar-icon" />
        Awards
      </Link>
    </li>
  ),
  quiz: () => (
    <li>
      <Link to="/quiz">
        <ListAltIcon className="nav-sidebar-icon" />
        Quiz
      </Link>
    </li>
  ),
  misc: () => (
    <li>
      <Link to="/misc">
        <ListAltIcon className="nav-sidebar-icon" />
        Misc
      </Link>
    </li>
  ),
  support: () => (
    <li>
      <a target="_blank" href="http://help.kluwell.com/help">
        <HelpIcon className="nav-sidebar-icon" />
        Support
      </a>
    </li>
  ),
  logout: ({onClick}) => (
    <li>
      <a href={null} onClick={onClick}>
        <ExitToAppIcon className="nav-sidebar-icon" />
        Logout
      </a>
    </li>
  ),
};

const navBarItems = {
  dashboard: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/">Dashboard</NavLink>
    </MenuItem>
  ),
  users: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/users">Users</NavLink>
    </MenuItem>
  ),
  schools: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/schools">Schools</NavLink>
    </MenuItem>
  ),
  classrooms: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/classrooms">Classrooms</NavLink>
    </MenuItem>
  ),
  adminReadings: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/readings">Classrooms</NavLink>
    </MenuItem>
  ),
  subscriptions: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/subscriptions">Subscriptions</NavLink>
    </MenuItem>
  ),
  orders: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/orders">Orders</NavLink>
    </MenuItem>
  ),
  messages: ({onClick, unreadCnt}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/messages">
        <Badge color="secondary" badgeContent={unreadCnt} invisible={!unreadCnt}>
          Messages
        </Badge>
      </NavLink>
    </MenuItem>
  ),
  receivedMessages: ({onClick, unreadCnt}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/messages/received">
        <Badge color="secondary" badgeContent={unreadCnt} invisible={!unreadCnt}>
          Messages
        </Badge>
      </NavLink>
    </MenuItem>
  ),
  readings: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/readings">Readings</NavLink>
    </MenuItem>
  ),
  books: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/books">Books</NavLink>
    </MenuItem>
  ),
  tips: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/tips">Tips</NavLink>
    </MenuItem>
  ),
  awards: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/awards">Awards</NavLink>
    </MenuItem>
  ),
  quiz: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/quiz">Quiz</NavLink>
    </MenuItem>
  ),
  misc: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/misc">Misc</NavLink>
    </MenuItem>
  ),
  support: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <a target="_blank" href="http://help.kluwell.com/help">
        Support
      </a>
    </MenuItem>
  ),
  account: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <NavLink to="/account">Account</NavLink>
    </MenuItem>
  ),
  logout: ({onClick}) => (
    <MenuItem onClick={onClick}>
      <a href={null}>Logout</a>
    </MenuItem>
  ),
};

const getNavKeys = (role) => {
  switch (role) {
    case 'kluwell_admin':
      return [
        'users',
        'schools',
        'classrooms',
        'adminReadings',
        'subscriptions',
        'orders',
        'messages',
        'books',
        'tips',
        'awards',
        'quiz',
        'misc',
      ];
    case 'school_coordinator':
      return ['users', 'classrooms', 'adminReadings', 'subscriptions', 'messages', 'books', 'tips', 'awards'];
    case 'teacher':
      return ['users', 'classrooms', 'adminReadings', 'messages', 'books', 'tips', 'awards'];
    case 'parent':
      return ['subscriptions', 'receivedMessages', 'tips'];
    case 'reader':
      return ['readings', 'receivedMessages', 'tips', 'awards'];
    default:
      throw Error(`Invalid role: ${role}`);
  }
};

export const NavBar = ({user, switchActiveSchool, switchActiveClassroom, handleLogout}) => {
  const {unreadCnt} = useContext(NotificationContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const navKeys = getNavKeys(user.role);

  const handleMenuClick = ({currentTarget}) => setAnchorEl(currentTarget);

  const handleMenuClose = () => setAnchorEl(null);

  const renderReaderActiveSchool = () => {
    const {activeSchool, activeClassroom, schools, classrooms} = user;
    const availableSchoolIds = new Set(classrooms.map((classroom) => classroom.schoolId));
    return (
      <div className="school-name" style={{display: 'flex'}}>
        <DropDownMenu
          buttonText={
            <React.Fragment>
              {activeSchool.name}
              {!activeSchool.isActive && <ArchivedStatus className="small-margin" isActive={false} isReader />}
            </React.Fragment>
          }
          buttonProps={{
            size: 'large',
            style: {
              color: '#fff',
              textTransform: 'initial',
              height: '100%',
              margin: '0 5px',
              padding: '0 10px',
            },
          }}
          items={[...schools.filter(({isActive}) => isActive), ...schools.filter(({isActive}) => !isActive)]
            .filter((school) => availableSchoolIds.has(school.id))
            .map((school) => ({
              value: school.id,
              label: (
                <div className="flex-row-space-between" style={{width: '100%'}}>
                  {school.name}
                  {<ArchivedStatus className="small-margin" isActive={school.isActive} isReader />}
                </div>
              ),
            }))}
          onSelect={switchActiveSchool}
        />
        -
        <DropDownMenu
          buttonText={
            <React.Fragment>
              {activeClassroom.name}
              {!activeClassroom.isActive && <ArchivedStatus className="small-margin" isActive={false} isReader />}
            </React.Fragment>
          }
          buttonProps={{
            size: 'large',
            style: {
              color: '#fff',
              textTransform: 'initial',
              height: '100%',
              margin: '0 5px',
              padding: '0 10px',
            },
          }}
          items={[...classrooms.filter(({isActive}) => isActive), ...classrooms.filter(({isActive}) => !isActive)]
            .filter((classroom) => classroom.schoolId === activeSchool.id)
            .map((school) => ({
              value: school.id,
              label: (
                <div className="flex-row-space-between" style={{width: '100%'}}>
                  {school.name}
                  {<ArchivedStatus className="small-margin" isActive={school.isActive} isReader />}
                </div>
              ),
            }))}
          onSelect={switchActiveClassroom}
        />
      </div>
    );
  };

  return (
    <Navbar key="nav-primary" className="nav-primary" inverse collapseOnSelect fluid>
      <Navbar.Header>
        <Navbar.Brand tabIndex="-1">
          <Link to="/">
            <img alt="Kluwell" src="/images/kluwell-header-logo.svg" />
          </Link>
        </Navbar.Brand>
        <IconButton className="navbar-toggle" onClick={handleMenuClick}>
          <Badge color="secondary" badgeContent={unreadCnt} invisible={!unreadCnt}>
            <MenuIcon />
          </Badge>
        </IconButton>
        {isReader(user.role) && user.activeSchool.name && renderReaderActiveSchool()}
        {isSchoolCoordinatorOrTeacher(user.role) && user.activeSchool.name && (
          <div className="school-name">
            <DropDownMenu
              buttonText={
                <React.Fragment>
                  {user.activeSchool.name}
                  {!user.activeSchool.isActive && (
                    <ArchivedStatus className="small-margin" isActive={user.activeSchool.isActive} />
                  )}
                </React.Fragment>
              }
              buttonProps={{
                fullWidth: true,
                size: 'large',
                style: {
                  color: '#fff',
                  textTransform: 'initial',
                },
              }}
              items={user.schools.map((school) => ({
                value: school.id,
                label: (
                  <div className="flex-row-space-between" style={{width: '100%'}}>
                    {school.name}
                    {<ArchivedStatus className="small-margin" isActive={school.isActive} />}
                  </div>
                ),
              }))}
              onSelect={switchActiveSchool}
            />
          </div>
        )}
        <Menu className="navbar-toggle-dropdown" anchorEl={anchorEl} open={!!anchorEl} onClose={handleMenuClose}>
          {React.createElement(navBarItems.dashboard, {onClick: handleMenuClose})}
          {navKeys.map((key) =>
            React.createElement(
              navBarItems[key],
              ['messages', 'receivedMessages'].includes(key)
                ? {key, unreadCnt, onClick: handleMenuClose}
                : {key, onClick: handleMenuClose},
            ),
          )}
          {React.createElement(navBarItems.support, {onClick: handleMenuClose})}
          {React.createElement(navBarItems.account, {onClick: handleMenuClose})}
          {React.createElement(navBarItems.logout, {onClick: handleLogout})}
        </Menu>
      </Navbar.Header>
    </Navbar>
  );
};

NavBar.propTypes = {
  user: PropTypes.object.isRequired,
  accountSocket: PropTypes.object,
  switchActiveSchool: PropTypes.func.isRequired,
  switchActiveClassroom: PropTypes.func.isRequired,
};

export const SideBar = ({user, accountSocket, handleLogout}) => {
  const {unreadCnt, setNotificationCount, increaseNotificationCount} = useContext(NotificationContext);

  useEffect(() => {
    fetchUnreadMessageCnt().then(({unreadCnt}) => setNotificationCount(unreadCnt));
    accountSocket.registerMessageHandler(() => {
      increaseNotificationCount(1);
      new Notification('You have a new notification');
    });
    return () => {
      accountSocket.unregisterMessageHandler();
    };
  }, []);

  return (
    <div className="nav-sidebar-wrapper">
      {!isEmpty(user) && (
        <Link className="current-user" to="/account/profile">
          {user.avatar ? (
            <ImageWrapper width={50} height={50} className="avatar-wrapper">
              <img alt="Avatar" src={`${process.env.SERVER_BASE_URL}${user.avatar}`} />
            </ImageWrapper>
          ) : (
            <div className="user-initials">
              {user.firstName ? user.firstName[0] : ''}
              {user.lastName ? user.lastName[0] : ''}
            </div>
          )}
          <div className="user-info">
            <div className="user-fullname">{buildUserFullName(user)}</div>
            <div className="user-additional-info">{formatRole(user.role)}</div>
          </div>
        </Link>
      )}
      <ul className="nav-sidebar">
        {React.createElement(sideBarItems.dashboard)}
        {getNavKeys(user.role).map((key) =>
          React.createElement(
            sideBarItems[key],
            ['messages', 'receivedMessages'].includes(key) ? {key, unreadCnt} : {key},
          ),
        )}
        {React.createElement(sideBarItems.support)}
        {React.createElement(sideBarItems.logout, {onClick: handleLogout})}
      </ul>
    </div>
  );
};
